/**
 * Quick-chat: the phrase menu and the speech bubbles.
 *
 * The whole feature is two small machines sharing a phrase book
 * (shared/phrases.js -- its header carries the safety argument; the short
 * version is that only an INDEX ever crosses the wire, so the street can
 * only ever say what the book says):
 *
 *   - THE MENU.  T opens it (or the "say" pad on a phone), one digit picks a
 *     group, one digit picks a phrase, Escape closes -- so a practised
 *     player says "Follow me!" in three keystrokes without the game ever
 *     taking the keyboard away from their feet: WASD deliberately keeps
 *     working while the menu is up, because choosing what to shout while
 *     running to the creek is the whole mood of the thing.  Every row is
 *     also a button, which is the entire touch story: same menu, same
 *     order, tapped instead of typed.
 *
 *   - THE BUBBLES.  Screen-space DOM, one element per speaking cat, placed
 *     every frame by projecting a point over the cat's head.  This is the
 *     opposite call from roster.js's name plates, and the difference is
 *     load-bearing, not taste: fifteen plates exist permanently and so had
 *     to be sprites (a permanent DOM plate re-layouts every frame for the
 *     life of the tab), but a bubble is RARE and BRIEF -- the say bucket
 *     caps the whole street at a handful visible at once -- and only DOM
 *     gives the three behaviours the brief thing actually needs: real text
 *     wrapping at a fixed em size regardless of distance (a sprite bubble
 *     shrinks with range until it is decoration, and this text exists to be
 *     READ), clamping against the screen edges, and cheap de-overlapping in
 *     pixels.  The layout cost a sprite avoids is exactly one measure per
 *     phrase SET (cached), never per frame -- per frame is transform and
 *     opacity only, the compositor-side properties.
 *
 * WHEN A BUBBLE HIDES rather than clamps: the speaker being hard to find is
 * fine, a bubble pointing at nothing is not.  So the bubble shows only
 * while its cat is roughly on screen (the anchor inside a slightly padded
 * frustum -- inside it, clamping keeps the text whole at the edges), nearer
 * than HIDE_DIST, and not behind level geometry (one collider-grid raycast
 * per visible bubble, the same query the camera runs every frame to keep
 * itself out of walls; terrain has no colliders, per the camera's own note,
 * so a grassy rise never occludes -- acceptable, since a cat behind a rise
 * is still where the bubble points).  Overlapping bubbles stack upward,
 * nearest cat keeping its true spot, so a spawn-point scrum reads as a
 * column of speech instead of a pile.
 *
 * THE LOCAL ECHO RULE: our own bubble is drawn at pick time, not when the
 * broadcast comes back -- zero latency, and it is all there is on the
 * offline build, where the game must stay a game.  The net facade drops the
 * broadcast echo for our own slot to match (net/index.js).  The price of
 * honesty for that instant feedback is the little bucket below: a mirror of
 * the server's say limiter, run BEFORE the phrase is shown or sent, so the
 * menu refuses what the server would silently drop and the player never
 * "says" something the street never heard.  The mirror can drift from the
 * server's bucket across a reconnect (the server's rides the cat and never
 * resets); the worst case of drift is one refusal too many or too few,
 * which is exactly the stake this duplication is priced for.
 */
import * as THREE from "three";
import { PHRASE_GROUPS, phraseText } from "@catworld/shared";
import { SAY_RATE, SAY_BURST } from "@catworld/shared";

/** How long a bubble stands, and how long the end of it takes.  4.5 s reads
    a 30-character line twice with room to find the cat first; the fade is
    slow enough to feel like trailing off rather than a cut. */
const SHOW_S = 4.5;
const FADE_S = 0.4;

/**
 * Where the bubble's tail points: metres above the cat's root.  The name
 * plate floats at 0.62 and the purr heart at 0.78 (roster.js), so 0.92
 * clears both -- speech, name, heart, cat, in reading order.  A constant
 * rather than scaled by the cat's `size` for the same reason the plates
 * divide the scale back out: every bubble on the street should hang at the
 * same height, or a chonky cat looks like it is shouting from a podium.
 */
const BUBBLE_Y = 0.92;

/** Past this the text would be over a cat a few pixels tall -- the words
    would read but the SPEAKER would not.  24 m is comfortably inside the
    fog's far ramp (38 m) and about the range at which the name plates stop
    being legible, which is the honest definition of "too far to talk". */
const HIDE_DIST = 24;

/** A raycast hit this far short of the cat counts as occlusion.  The slack
    absorbs the fence the cat is sitting BESIDE clipping the ray's last half
    metre; anything shorter is a wall genuinely in front of the speaker. */
const OCCLUDE_SLACK = 0.6;

/** The anchor may drift this far outside the NDC frustum before the bubble
    hides instead of clamping: at 1.15 the cat is just off the edge and the
    clamped bubble still points somewhere true ("just off screen, this
    side"); much past that it is pointing at a rumour. */
const EDGE_NDC = 1.15;

const MARGIN = 8;       // px between a clamped bubble and the screen edge
const TAIL_PX = 6;      // the CSS tail's height; the box sits this far above the anchor
const STACK_GAP = 6;    // px between bubbles stacked by the de-overlapper

/** How long the "catching my breath" note stands after the bucket refuses a
    phrase.  Long enough to read, short enough to be gone before the bucket
    itself is. */
const NOTE_S = 1.6;

export class QuickChat {
  /**
   * @param menu      the #chat panel (may be null on a stale page; the
   *                  feature then simply does not exist, like the meters)
   * @param layer     the #bubbles fixed layer the bubble divs live in
   * @param camera    the game camera, projected against AFTER updateCamera
   *                  has posed it for the frame (main.js calls update there)
   * @param grid      the ColliderGrid, for the occlusion raycast
   * @param getNet    () => Net | null.  A thunk, not the object: chat is
   *                  built at module load and Net several seconds later, so
   *                  update() wires the onSay callback the first frame the
   *                  facade exists.
   * @param anchorFor (slot, out{x,y,z}) => bool.  Slot -1 is the local cat.
   *                  False means "no such cat on screen this frame" and the
   *                  bubble simply hides until it times out -- a cat that
   *                  left mid-sentence takes its sentence with it.
   * @param blocked   () => bool -- a real panel (builder, join card) owns
   *                  the screen; the menu neither opens over one nor stays
   *                  open under one.
   */
  constructor({ menu, layer, camera, grid, getNet, anchorFor, blocked = () => false }) {
    this.menu = menu;
    this.layer = layer;
    this.camera = camera;
    this.grid = grid;
    this.getNet = getNet;
    this.anchorFor = anchorFor;
    this.blocked = blocked;

    this.isOpen = false;
    this.group = -1;               // which group the list view shows; -1 = groups view
    this._net = null;              // the facade once wired, so wiring runs once

    /** slot (or -1 for the local cat) -> { el, text, w, h, left, ... }.
        One bubble per cat by construction: a new phrase replaces the old,
        because two sentences over one head is already a pile of two. */
    this.bubbles = new Map();

    // The client mirror of limits.js's say bucket -- same refill arithmetic,
    // same constants, reimplemented in six lines rather than imported
    // because limits.js is server code and the game bundle must not grow a
    // dependency on it for a fraction this small.
    this._tokens = SAY_BURST;
    this._refillAt = performance.now();
    this._noteFor = 0;

    /** Flat index of each group's first phrase, so a (group, digit) pick
        maps to the wire's single index without searching. */
    this._base = [];
    let base = 0;
    for (const g of PHRASE_GROUPS) {
      this._base.push(base);
      base += g.phrases.length;
    }

    // Projection scratch, reused every frame for every bubble: sixteen
    // bubbles at display rate must not allocate (the roster's rule, same
    // reason).
    this._w = new THREE.Vector3();
    this._c = new THREE.Vector3();
    this._ray = new THREE.Ray();
    this._anchor = { x: 0, y: 0, z: 0 };

    if (this.menu) this._build();

    // Capture phase, which for a window-target listener runs before
    // main.js's bubble-phase keydown: an open menu must eat its digits
    // before the debug overlay's cat-switcher can read 3 as "become Grey".
    // Everything the menu does not consume falls through untouched -- WASD
    // keeps walking, per the header.
    addEventListener("keydown", (e) => this._onKey(e), true);
  }

  // --- the menu --------------------------------------------------------------

  /**
   * Build the panel from the phrase book, the happyhud bargain: the page
   * can never drift out of step with the table, because the page IS the
   * table.  textContent throughout, never innerHTML with table strings --
   * the table is ours today, but the habit is the point.
   */
  _build() {
    const m = this.menu;
    const head = document.createElement("div");
    head.className = "chat-head";
    this._title = document.createElement("span");
    this._title.className = "label";
    this._note = document.createElement("span");
    this._note.className = "label chat-note";
    this._note.hidden = true;
    head.append(this._title, this._note);

    this._groupsEl = document.createElement("div");
    this._groupsEl.className = "chat-rows";
    PHRASE_GROUPS.forEach((g, gi) => {
      this._groupsEl.append(this._row(String(gi + 1), g.label, () => this._showGroup(gi)));
    });

    this._listEl = document.createElement("div");
    this._listEl.className = "chat-rows";
    this._listEl.hidden = true;

    m.append(head, this._groupsEl, this._listEl);
    this._showGroups();

    // join.js's isolation idiom: main.js listens on window for the events
    // that drive the game, and picking a phrase must not also swipe, look,
    // or start anything.  mouseup stays unstopped for the reason panel.js
    // and join.js both leave it: the drag-look flag must always clear.
    for (const type of ["keyup", "pointerdown", "mousedown", "wheel", "touchstart"]) {
      m.addEventListener(type, (e) => e.stopPropagation());
    }
  }

  /** One menu row: a keycap chip and a label, as a real button so the touch
      path is just "tap the row" with no second implementation. */
  _row(chip, label, onPick) {
    const b = document.createElement("button");
    b.type = "button";
    const keycap = document.createElement("b");
    keycap.textContent = chip;
    const span = document.createElement("span");
    span.textContent = label;
    b.append(keycap, span);
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      onPick();
    });
    return b;
  }

  _showGroups() {
    this.group = -1;
    this._title.textContent = "say something";
    this._groupsEl.hidden = false;
    this._listEl.hidden = true;
  }

  _showGroup(gi) {
    const g = PHRASE_GROUPS[gi];
    if (!g) return;
    this.group = gi;
    this._title.textContent = g.label;
    this._listEl.replaceChildren(
      ...g.phrases.map((p, j) => this._row(String(j + 1), p, () => this._pick(this._base[gi] + j))),
      this._row("0", "back", () => this._showGroups()),
    );
    this._groupsEl.hidden = true;
    this._listEl.hidden = false;
  }

  open() {
    if (!this.menu || this.isOpen || this.blocked()) return;
    this.isOpen = true;
    this._note.hidden = true;
    this._showGroups();
    this.menu.classList.remove("hidden");
  }

  close() {
    if (!this.menu || !this.isOpen) return;
    this.isOpen = false;
    this.menu.classList.add("hidden");
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  _onKey(e) {
    if (!this.menu || e.repeat) return;

    if (!this.isOpen) {
      if (e.code !== "KeyT" || this.blocked()) return;
      // Belt and braces over blocked(): a T typed into any text field --
      // today's are all inside panels blocked() covers, but the next one
      // may not be -- must stay a letter, never a menu.
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      e.stopPropagation();
      e.preventDefault();
      this.open();
      return;
    }

    if (e.code === "Escape" || e.code === "KeyT") {
      // Escape closes outright, per the spec.  (Under pointer lock the
      // browser spends Escape on releasing the lock and this handler never
      // hears it, which is why T closes too.)
      e.stopPropagation();
      this.close();
      return;
    }
    if (e.code === "Backspace" && this.group >= 0) {
      e.stopPropagation();
      e.preventDefault();
      this._showGroups();
      return;
    }
    const m = /^(?:Digit|Numpad)(\d)$/.exec(e.code);
    if (!m) return;                     // not ours: keep walking, keep looking
    e.stopPropagation();
    e.preventDefault();
    const n = Number(m[1]);
    if (this.group < 0) {
      if (n >= 1 && n <= PHRASE_GROUPS.length) this._showGroup(n - 1);
    } else if (n === 0) {
      this._showGroups();
    } else if (n <= PHRASE_GROUPS[this.group].phrases.length) {
      this._pick(this._base[this.group] + n - 1);
    }
  }

  /** A phrase was chosen.  Bucket first, then bubble, then wire, then close
      -- in that order, so what the player sees is exactly what was sent. */
  _pick(i) {
    if (!this._take()) {
      this._note.textContent = "catching my breath…";
      this._note.hidden = false;
      this._noteFor = NOTE_S;
      return;                           // menu stays up; the pick can be retried
    }
    this._show(-1, i);
    this.getNet?.()?.say(i);
    this.close();
  }

  /** limits.js's TokenBucket.take(), condensed; see the header for why it
      is mirrored rather than imported. */
  _take() {
    const now = performance.now();
    this._tokens = Math.min(
      SAY_BURST,
      this._tokens + (Math.max(0, now - this._refillAt) / 1000) * SAY_RATE,
    );
    this._refillAt = now;
    if (this._tokens < 1) return false;
    this._tokens -= 1;
    return true;
  }

  // --- the bubbles -----------------------------------------------------------

  /** Put a phrase over a cat.  Slot -1 is the local cat; remote slots
      arrive via the net facade's onSay (wired lazily in update). */
  _show(slot, i) {
    if (!this.layer) return;
    const text = phraseText(i);
    if (text === null) return;          // a build newer than ours spoke: silence, not garbage

    let b = this.bubbles.get(slot);
    if (!b) {
      const el = document.createElement("div");
      el.className = "bubble";
      this.layer.append(el);
      b = { el, text: null, w: 0, h: 0, left: 0, x: 0, y: 0, dist: 0, shown: false };
      this.bubbles.set(slot, b);
    }
    if (b.text !== text) {
      b.text = text;
      b.el.textContent = text;
      // Measured once per phrase SET -- the one forced layout this system
      // ever pays.  Every frame after this touches transform and class
      // lists only.
      b.w = b.el.offsetWidth;
      b.h = b.el.offsetHeight;
    }
    b.left = SHOW_S;
    b.el.classList.remove("fade");
  }

  /**
   * Once per frame, from main.js AFTER updateCamera: age the bubbles,
   * project the live ones, unpile them, write the styles.  Called after the
   * camera is posed for this frame on purpose -- projected a frame early
   * the bubbles trail every pan by 16 ms, which reads as the words being
   * taped on rather than belonging to the cat.
   */
  update(dt) {
    // The facade appears seconds after this object; wire the remote path
    // the first frame it exists.  (Assignable-callback contract; the field's
    // comment in net/index.js says why it is not a constructor callback.)
    const net = this.getNet ? this.getNet() : null;
    if (net && net !== this._net) {
      this._net = net;
      net.onSay = (slot, p) => this._show(slot, p);
    }

    // A real panel taking the screen closes the menu under it, the same
    // moment main.js's panelled edge releases the touch inputs.
    if (this.isOpen && this.blocked()) this.close();

    if (this._noteFor > 0) {
      this._noteFor -= dt;
      if (this._noteFor <= 0) this._note.hidden = true;
    }

    if (this.bubbles.size === 0) return;

    const placed = [];
    for (const [slot, b] of this.bubbles) {
      b.left -= dt;
      if (b.left <= 0) {
        b.el.remove();
        this.bubbles.delete(slot);
        continue;
      }
      if (b.left <= FADE_S) b.el.classList.add("fade");
      if (this._place(slot, b)) {
        placed.push(b);
      } else if (b.shown) {
        b.shown = false;
        b.el.classList.remove("show");
      }
    }
    if (placed.length === 0) return;

    // Unpile.  Nearest cat keeps its true spot (its bubble is the one the
    // player is most likely looking at); everything farther climbs over
    // whatever it landed on.  The while() re-checks after each move because
    // climbing over one bubble can land on another; it terminates because y
    // only ever decreases and each pass that moves nothing exits.  A tall
    // enough stack can climb off the top of the screen unclamped -- with
    // the say bucket that takes a coordinated crowd, and "the pile stays
    // readable" is the promise being kept, not "every bubble stays on
    // screen".
    placed.sort((a, b) => a.dist - b.dist);
    for (let i = 1; i < placed.length; i++) {
      const b = placed[i];
      let guard = placed.length + 1;
      let moved = true;
      while (moved && guard-- > 0) {
        moved = false;
        for (let j = 0; j < i; j++) {
          const o = placed[j];
          if (b.x - b.w / 2 < o.x + o.w / 2 && b.x + b.w / 2 > o.x - o.w / 2 &&
              b.y - b.h < o.y && b.y > o.y - o.h) {
            b.y = o.y - o.h - STACK_GAP;
            moved = true;
          }
        }
      }
    }

    for (const b of placed) {
      // The -50%/-100% pair centres the box over the anchor and sits its
      // bottom edge on it; the pixel pair is the only part that changes.
      b.el.style.transform = `translate(${b.x}px, ${b.y}px) translate(-50%, -100%)`;
      if (!b.shown) {
        b.shown = true;
        b.el.classList.add("show");
      }
    }
  }

  /**
   * One bubble's screen position for this frame, or false to hide it.
   * b.x is the box's centre; b.y its bottom edge (the tail tip sits TAIL_PX
   * below that, on the projected anchor when nothing clamped).
   */
  _place(slot, b) {
    if (!this.anchorFor(slot, this._anchor)) return false;
    const cam = this.camera;
    const a = this._anchor;
    this._w.set(a.x, a.y + BUBBLE_Y, a.z);

    // Camera space first: "behind the lens" has to be answered before
    // project(), whose perspective divide folds back-of-camera points into
    // plausible on-screen coordinates instead of rejecting them.
    this._c.copy(this._w).applyMatrix4(cam.matrixWorldInverse);
    if (this._c.z > -0.05) return false;

    const dist = this._w.distanceTo(cam.position);
    if (dist > HIDE_DIST) return false;

    this._ray.origin.copy(cam.position);
    this._ray.direction.copy(this._w).sub(cam.position).divideScalar(dist);
    if (this.grid && this.grid.raycast(this._ray, dist) < dist - OCCLUDE_SLACK) return false;

    this._w.project(cam);
    if (Math.abs(this._w.x) > EDGE_NDC || Math.abs(this._w.y) > EDGE_NDC) {
      // The anchor hangs 0.92 m over the head, so a camera pitched down onto
      // a NEARBY cat pushes it off the top of the frame while the cat itself
      // fills the screen -- watched happening for whole seconds in the
      // browser harness, two cats chatting at the spawn.  The cat gets the
      // casting vote: body in frame, bubble clamps to the edge; body out,
      // bubble hides.  Chest height rather than the root because paws
      // exactly at the bottom edge is still "I can see the cat".
      this._c.set(a.x, a.y + 0.3, a.z).project(cam);
      if (Math.abs(this._c.x) > 1 || Math.abs(this._c.y) > 1) return false;
    }

    const W = innerWidth, H = innerHeight;
    b.x = clamp((this._w.x * 0.5 + 0.5) * W, b.w / 2 + MARGIN, W - b.w / 2 - MARGIN);
    b.y = clamp((0.5 - this._w.y * 0.5) * H - TAIL_PX, b.h + MARGIN, H - MARGIN);
    b.dist = dist;
    return true;
  }
}

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}
